import { router } from "@inertiajs/react"
import { useMemo } from "react"

import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnContent,
  KanbanColumnHeader,
  KanbanItem,
  KanbanOverlay,
} from "@/components/ui/kanban"
import { Meter } from "@/components/steel"
import { PROJECT_STAGES, type ProjectStage } from "@/lib/projects"
import { formatCurrency } from "@/lib/utils"

export type KanbanProject = {
  id: string
  name: string
  location: string
  status: string
  stage: ProjectStage
  initialBudget: number
  currentSpend: number
  totalRevenue: number
  teamSize: number
}

export function ProjectsKanban({ projects }: { projects: KanbanProject[] }) {
  const columns = useMemo(() => {
    const result = Object.fromEntries(
      PROJECT_STAGES.map((s) => [s.id, [] as KanbanProject[]]),
    ) as Record<ProjectStage, KanbanProject[]>

    for (const project of projects) result[project.stage]?.push(project)
    return result
  }, [projects])

  const handleValueChange = (next: Record<string, KanbanProject[]>) => {
    for (const [stage, stageProjects] of Object.entries(next)) {
      for (const project of stageProjects) {
        if (project.stage !== stage) {
          router.patch(`/projects/${project.id}`, { stage }, { preserveScroll: true })
        }
      }
    }
  }

  return (
    <Kanban
      value={columns}
      onValueChange={handleValueChange}
      getItemValue={(project) => project.id}
      className="flex gap-px bg-border"
    >
      <KanbanBoard className="gap-px overflow-x-auto border-b border-border bg-border pb-0">
        {PROJECT_STAGES.map((stage) => (
          <KanbanColumn
            key={stage.id}
            value={stage.id}
            className="w-[238px] min-h-[calc(100vh-53px)] rounded-none border-0 bg-background px-3 py-3.5"
          >
            <KanbanColumnHeader className="flex items-center justify-between border-0 p-0 pb-2.5">
              <span className="flex items-center gap-1.5 text-label font-semibold">
                <span
                  className="h-[5px] w-[5px] rounded-full bg-foreground"
                  style={{ opacity: stage.opacity }}
                />
                {stage.label}
              </span>
              <span className="num text-meta text-muted-foreground">{columns[stage.id].length}</span>
            </KanbanColumnHeader>

            <KanbanColumnContent value={stage.id} className="gap-2 p-0">
              {columns[stage.id].map((project) => {
                const pct = project.initialBudget > 0
                  ? (project.currentSpend / project.initialBudget) * 100
                  : 0

                return (
                  <KanbanItem
                    key={project.id}
                    value={project.id}
                    className="rounded-lg border border-border bg-card px-3 py-2.5 shadow-none hover:bg-secondary"
                    onClick={() => router.visit(`/projects/${project.id}`)}
                  >
                    <p className="mb-2 text-body font-medium leading-snug">{project.name}</p>
                    <p className="mb-2.5 text-meta text-muted-foreground">{project.location}</p>
                    <div className="flex items-center justify-between text-label">
                      <span className="num font-medium text-whisper">
                        {formatCurrency(project.totalRevenue)}
                      </span>
                      <span className="num text-muted-foreground">{Math.round(pct)}% used</span>
                    </div>
                    <div className="mt-2">
                      <Meter pct={pct} />
                    </div>
                  </KanbanItem>
                )
              })}
            </KanbanColumnContent>
          </KanbanColumn>
        ))}
      </KanbanBoard>
      <KanbanOverlay />
    </Kanban>
  )
}
